import type { ReactNode } from 'react'

import _ from 'lodash'
import { useFormContext } from 'react-hook-form'

import { useAuthContext } from '@/features/auth/model/auth-context'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { Button } from '@/shared/ui'
import { Field, PasswordField, TextField } from '@/shared/ui/Form'

type SignInFieldsProps = {
  children?: ReactNode
}

/**
 * Campos de acesso.
 *
 * O alternar visibilidade da senha era montado aqui, com um `InputGroup` e um
 * botão de ícone cujo rótulo não acompanhava o estado. Agora vem do
 * `PasswordField`, que já resolve nome acessível e alvo de toque — o campo
 * deixa de reimplementar o controle.
 */
export function SignInFields({ children }: SignInFieldsProps) {
  const { goToForgotPassword, config } = useAuthContext()
  const { getValues } = useFormContext()
  const { t } = useTranslation()

  const loginLabel = _.get(config, 'signIn.loginLabel', 'Login') as string
  const hideForgotPassword = !_.has(config, 'forgotPassword')

  return (
    // Ritmo vertical pelo container, não por margem no filho (MASTER §7 L4).
    <div className="flex flex-col gap-4">
      <Field name="login">
        <Field.Label>{loginLabel}</Field.Label>
        <Field.Control>
          <TextField autoComplete="username" />
        </Field.Control>
        <Field.Error />
      </Field>

      <Field name="password">
        <Field.Label>{t('auth.password', {}, { fallback: 'Senha' })}</Field.Label>
        <Field.Control>
          <PasswordField autoComplete="current-password" />
        </Field.Control>
        <Field.Error />
      </Field>

      {!hideForgotPassword && (
        <div className="flex flex-wrap justify-between gap-3">
          <Button
            type="button"
            variant="link"
            className="ml-auto h-auto p-0 text-sm font-bold"
            onClick={() => goToForgotPassword({ email: getValues('login') })}
          >
            {t('auth.forgotMyPassword', {}, { fallback: 'Esqueceu a senha?' })}
          </Button>
        </div>
      )}
      {children}
    </div>
  )
}
